/**
 * @file frame-draft.ts
 * @description Черновик шага «Кадр под блок» (phase19.16, партия SB120,
 * этап 1; uiux-sb120-stage1 §2.4 и §3; данные db-design-sb120-stage1 §1).
 * Один шаг на два места: фото фона секции (точка, масштаб, своё фото
 * телефона) и элемент «Фото» в колонке (точка и пропорции; масштаба в схеме
 * элемента нет). Здесь — перевод «значение ↔ черновик», «Пропустить» и
 * рамка элемента по доле колонки × высоте. Файл не режется.
 */

import type { SectionElementOf, SectionMediaBg } from "@chaiberi/shared";

import { PHONE_FRAME_ASPECT, type Device } from "./look-size";

type Image = SectionElementOf<"image">;
/** Точка фокуса, % от левого верхнего угла (как `sectionFocusSchema`). */
type SectionFocus = SectionMediaBg["focus"];

/** Что меняет шаг «Кадр»; нет ключа — «как на ПК» / «как у файла» / масштаб 1. */
export interface FrameDraft {
  focus: SectionFocus;
  focusPhone?: SectionFocus;
  zoom?: number;
  zoomPhone?: number;
  aspect?: number;
  aspectPhone?: number;
  mediaIdPhone?: string;
}

/** Необязательные ключи кадра. */
type FrameKey = Exclude<keyof FrameDraft, "focus">;

/** Записать черновик в объект: заданные ключи — значения, снятые — удалить. */
function apply<T extends object>(target: T, draft: FrameDraft, keys: ReadonlyArray<FrameKey>): T {
  const next: Record<string, unknown> = { ...target, focus: draft.focus };
  for (const key of keys) {
    if (draft[key] === undefined) delete next[key];
    else next[key] = draft[key];
  }
  return next as T;
}

/** Не копирует отсутствующие ключи: черновик без `undefined`. */
function pick(source: Partial<FrameDraft> & { focus: SectionFocus }, keys: ReadonlyArray<FrameKey>): FrameDraft {
  const out: FrameDraft = { focus: source.focus };
  for (const key of keys) if (source[key] !== undefined) Object.assign(out, { [key]: source[key] });
  return out;
}

const BG_KEYS = ["focusPhone", "zoom", "zoomPhone", "mediaIdPhone"] as const;
const IMAGE_KEYS = ["focusPhone", "aspect", "aspectPhone"] as const;

export const bgToFrame = (bg: SectionMediaBg): FrameDraft => pick(bg, BG_KEYS);
export const frameToBg = (bg: SectionMediaBg, draft: FrameDraft): SectionMediaBg => apply(bg, draft, BG_KEYS);
export const imageToFrame = (el: Image): FrameDraft => pick({ ...el, focus: el.focus ?? { x: 50, y: 50 } }, IMAGE_KEYS);
export const frameToImage = (el: Image, draft: FrameDraft): Image => apply(el, draft, IMAGE_KEYS);

/** «Пропустить»: фокус по центру, масштаб 1, своей точки телефона нет; пропорции и фото телефона остаются. */
export function skippedFrame(draft: FrameDraft): FrameDraft {
  const next: FrameDraft = { ...draft, focus: { x: 50, y: 50 } };
  delete next.focusPhone;
  delete next.zoom;
  delete next.zoomPhone;
  return next;
}

/**
 * Рамка фото в колонке (§3): ширина — доля колонки от ширины блока (на
 * телефоне колонки стопкой — 360), высота — по режиму: «По колонке» —
 * «не меньше» (256), «Числом» — число (на телефоне своё или × доля);
 * «По фото» — пропорции кадра черновика или файла.
 */
export function imageFrameAspect(el: Image, draft: FrameDraft, device: Device, columnWidth: number, fileAspect: number | null, phoneFactor: number): number {
  const width = device === "phone" ? 360 : Math.max(1, columnWidth);
  const h = el.height;
  if (h.kind === "column") return width / (h.min ?? 256);
  if (h.kind === "number") {
    const px = device === "phone" ? (h.valuePhone ?? h.value * phoneFactor) : h.value;
    if (px > 0) return width / px;
  }
  const own = device === "phone" ? (draft.aspectPhone ?? draft.aspect) : draft.aspect;
  return own ?? fileAspect ?? (device === "phone" ? PHONE_FRAME_ASPECT : 1.5);
}
